import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AnnualMinimumDetails } from '../../../../../models/transactions/annual-minimum-details';
import { SystemBanner } from '../../../../../models/shared/system-banner';
import { DateUtils } from '../../../../../utils/date-utils';
import { PortalService } from '../../../../../services/portal/portal.service';

@Component({
  selector: 'app-vb-payment-change-submission-age-for-minimum-calculation-card',
  templateUrl: './vb-payment-change-submission-age-for-minimum-calculation-card.component.html',
  styleUrls: ['./vb-payment-change-submission-age-for-minimum-calculation-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class VbPaymentChangeSubmissionAgeForMinimumCalculationCardComponent implements OnChanges {
  @Input({ required: true }) currentAnnualMinimumDetails!: AnnualMinimumDetails;
  @Input({ required: true }) newAnnualMinimumDetails!: AnnualMinimumDetails;

  private portalService = inject(PortalService);

  public detailsHaveChanged = false;
  public effectiveYear = 0;

  private currentDetails$ = new BehaviorSubject<AnnualMinimumDetails | null>(null);
  private newDetails$ = new BehaviorSubject<AnnualMinimumDetails | null>(null);

  public currentLabel$ = combineLatest([this.currentDetails$, this.portalService.isMember$]).pipe(
    map(([details, isMember]) => this.buildLabel(details, isMember))
  );

  public newLabel$ = combineLatest([this.newDetails$, this.portalService.isMember$]).pipe(
    map(([details, isMember]) => this.buildLabel(details, isMember))
  );

  public changeBanner!: SystemBanner;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.currentAnnualMinimumDetails) {
      this.currentDetails$.next(this.currentAnnualMinimumDetails ?? null);
    }

    if (changes.newAnnualMinimumDetails) {
      this.newDetails$.next(this.newAnnualMinimumDetails ?? null);
      // TODO: SPP-3206 submission details don't return the year the election takes effect, hardcoded for now
      this.effectiveYear = this.newAnnualMinimumDetails?.effectiveYear ?? DateUtils.currentYearInCST() + 1;
      this.changeBanner = {
        bannerType: 'warning',
        text: $localize`Changes to the age for minimum calculation come into effect on January 1, ${this.effectiveYear}:year:. Annual minimum values for that year, and future years, will be impacted.`,
        layout: 'contained',
        isDismissible: false
      } as SystemBanner;
    }

    if (this.currentAnnualMinimumDetails && this.newAnnualMinimumDetails) {
      this.detailsHaveChanged =
        this.currentAnnualMinimumDetails.minimumAgeSpouseId !== this.newAnnualMinimumDetails.minimumAgeSpouseId;
    }
  }

  private buildLabel(details: AnnualMinimumDetails | null, isMember: boolean): string {
    if (!details) {
      return '';
    }
    if (isMember) {
      return details.isSpouse ? $localize`Use My Spouse's Age` : $localize`Use My Age`;
    }
    return details.isSpouse ? $localize`Use Spouse's Age` : $localize`Use Member's Age`;
  }
}
